// The in-app steering-decision card (Brief 5 pt17) — a pick-one control that
// renders INLINE in the chat stream (a `Conversation` row) when Signal surfaces
// a decision: the prompt (a fact + the ask), an honest framing line, up to three
// operator-generated option suggestions, and an always-present "other" free-text.
//
// A11y: it is a real single-choice control —
//   * the options are a `role="radiogroup"` (labelled by the prompt) of
//     `role="radio"` items with ROVING tabindex: the group is ONE tab stop,
//     arrow keys move the selection, Tab leaves to the "other" textarea. Enter/
//     Space picks the focused option.
//   * the selected state carries a non-colour mark (a filled dot) so it doesn't
//     rely on the accent hue (WCAG 1.4.1).
//   * it is NOT a modal — no focus trap; the reader can Tab straight past it to
//     the composer. Live announcements ride the message list's existing
//     `aria-live` region (no second one).
//   * it collapses to a non-interactive "Answered" summary the instant the
//     decision is resolved on ANY surface (the resolved marker), without yanking
//     focus.
// Token-pure (var(--waytide-sys-*)); the reserved amber `signal-attention` is
// NOT used here — a decision card is a consequence of needs-me, not a second
// needs-me. Signal's accent (watch-signal) tints the left rule.

import { useRef, useState } from "react";
import { Radio } from "lucide-react";
import type { SteeringDecision } from "@waytide/core";
import { pickSteeringDecision } from "../api/client";

export function SteeringDecisionCard({
  decision,
  resolved,
  onPick,
}: {
  decision: SteeringDecision;
  /** True once the decision is answered (on this or any surface) — render the
   * collapsed summary, no longer interactive. */
  resolved: boolean;
  /** Optional override for the pick wire (the test seam); defaults to the real
   * client. Resolves when the pick has been recorded. */
  onPick?: (optionId: string | null, other: string | null) => Promise<void>;
}) {
  // The option currently FOCUSED in the radiogroup (roving tabindex). Defaults
  // to the first option so the group has a single tab stop with a clear target.
  const [focused, setFocused] = useState(0);
  const [other, setOther] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const radioRefs = useRef<(HTMLDivElement | null)[]>([]);

  const send = onPick ?? defaultPick(decision.decision_id);

  async function pick(optionId: string | null, otherText: string | null) {
    if (submitting || resolved) return;
    setSubmitting(true);
    setError(null);
    try {
      await send(optionId, otherText);
      // The resolved marker fans out over SSE and flips `resolved` → the card
      // collapses; no local state flip needed (one source of truth).
    } catch {
      setError("Couldn't record that — try again.");
      setSubmitting(false);
    }
  }

  // Collapsed, non-interactive summary once answered (on any surface).
  if (resolved) {
    return (
      <div className="wt-steer wt-steer--resolved" data-testid="steering-resolved">
        <p className="wt-steer__prompt">{decision.prompt}</p>
        <p className="wt-steer__answered">Answered — it's in the session.</p>
      </div>
    );
  }

  const promptId = `wt-steer-prompt-${decision.decision_id}`;

  function onRadioKey(e: React.KeyboardEvent, index: number) {
    const count = decision.options.length;
    if (e.key === "ArrowDown" || e.key === "ArrowRight") {
      e.preventDefault();
      const next = (index + 1) % count;
      setFocused(next);
      radioRefs.current[next]?.focus();
    } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
      e.preventDefault();
      const prev = (index - 1 + count) % count;
      setFocused(prev);
      radioRefs.current[prev]?.focus();
    } else if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      void pick(decision.options[index].id, null);
    }
  }

  return (
    <div className="wt-steer" data-testid="steering-card">
      <p className="wt-steer__prompt" id={promptId}>
        {decision.prompt}
      </p>
      <p className="wt-steer__frame">Your call — here's how I'd cut it:</p>
      <div
        className="wt-steer__options"
        role="radiogroup"
        aria-labelledby={promptId}
      >
        {decision.options.map((opt, i) => {
          const selected = i === focused;
          return (
            <div
              key={opt.id}
              ref={(el) => {
                radioRefs.current[i] = el;
              }}
              role="radio"
              aria-checked={selected}
              tabIndex={selected ? 0 : -1}
              className="wt-steer__option"
              data-testid="steering-option"
              data-selected={selected ? "true" : undefined}
              onFocus={() => setFocused(i)}
              onKeyDown={(e) => onRadioKey(e, i)}
              onClick={() => void pick(opt.id, null)}
            >
              <span className="wt-steer__dot" aria-hidden="true" />
              <span className="wt-steer__label">{opt.label}</span>
            </div>
          );
        })}
      </div>
      <form
        className="wt-steer__other"
        onSubmit={(e) => {
          e.preventDefault();
          if (other.trim()) void pick(null, other.trim());
        }}
      >
        <label className="wt-steer__other-label" htmlFor={`${promptId}-other`}>
          Something else
        </label>
        <textarea
          id={`${promptId}-other`}
          className="wt-steer__other-input"
          rows={1}
          value={other}
          placeholder="Tell me what you'd rather do…"
          onChange={(e) => setOther(e.target.value)}
          disabled={submitting}
        />
        <button
          type="submit"
          className="wt-steer__other-send"
          disabled={!other.trim() || submitting}
        >
          Send
        </button>
      </form>
      {error ? (
        <p className="wt-steer__error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** The little Signal mark on a decision card head — the signal operator raising
 * the question. Exported so `Conversation` can render it in the message head. */
export function SteeringMark() {
  return <Radio className="wt-dash-msg__mark" size={13} aria-hidden="true" />;
}

/** Default pick wire: post to the real client's pick route. An option id sends
 * `{ option_id }`; the typed `other` sends `{ other }`. */
function defaultPick(decisionId: number) {
  return (optionId: string | null, other: string | null) =>
    pickSteeringDecision(
      decisionId,
      optionId != null ? { option_id: optionId } : { other: other ?? "" },
    );
}
